import { Link } from 'react-router-dom'
import { ArrowLeftIcon, ChevronRightIcon } from 'lucide-react'

import type {
  CustomAgentRunStatus,
  CustomAgentTriggerKind
} from '@axilon-apps/domains-ontology-ui-state'

import { CopyButton } from '../../components/Chat/CopyButton'
import { StatusPill } from '../../components/primitives/StatusPill'
import { customAgentConfigHref } from '../../lib/component-links'
import { formatRelativeTime } from '../../lib/time'
import { cn } from '../../lib/utils'
import { FindingStatusStrip } from './FollowUpBanners'
import { findingVerdictView } from './completion-verdict'
import { RunCancelButton } from './RunCancelButton'
import { isLiveRunState, runStatusView } from './run-status'
import {
  describeTriggerKind,
  formatAbsoluteTime,
  formatRunDuration,
  shortRunId
} from './run-timing'

interface Props {
  title: string
  configId?: string | null
  configName?: string | null
  runId: string
  state: CustomAgentRunStatus
  triggerKind: CustomAgentTriggerKind
  completionStatus?: string | null
  completionReason?: string | null
  completedAt?: string | null
  createdAt: string
  startedAt?: string | null
  finishedAt?: string | null
  invocationCount: number
  now: number
  cancelling: boolean
  onCancel?: () => Promise<void>
  canConfigure?: boolean
}

export function RunPageHeader({
  title,
  configId,
  configName,
  runId,
  state,
  triggerKind,
  completionStatus,
  completionReason,
  completedAt,
  createdAt,
  startedAt,
  finishedAt,
  invocationCount,
  now,
  cancelling,
  onCancel,
  canConfigure = false
}: Props) {
  const { tone, label } = runStatusView(state)
  const findingVerdict = triggerKind === 'condition' ? findingVerdictView(completionStatus) : null
  const live = isLiveRunState(state)
  const duration = formatRunDuration(startedAt, finishedAt, now)
  const durationLabel =
    duration === '—' ? null : state === 'running' ? `running for ${duration}` : `ran ${duration}`
  const callsLabel =
    invocationCount === 0 && !live
      ? null
      : `${invocationCount} tool call${invocationCount === 1 ? '' : 's'}${live ? ' so far' : ''}`

  const createdRelative = formatRelativeTime(createdAt, { now, fallback: '' })
  const items: { text: string; title?: string }[] = [{ text: describeTriggerKind(triggerKind) }]
  if (createdRelative) items.push({ text: createdRelative, title: formatAbsoluteTime(createdAt) })
  if (durationLabel) items.push({ text: durationLabel })
  if (callsLabel) items.push({ text: callsLabel })

  return (
    <header className="flex flex-col gap-3">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-12 text-ink-3">
        <Link
          to={canConfigure ? '/configure?type=custom' : '/chat'}
          className="inline-flex items-center gap-1 rounded px-1 -mx-1 hover:text-ink-2 hover:underline hover:underline-offset-2"
        >
          <ArrowLeftIcon className="size-3" aria-hidden />
          {canConfigure ? 'Configure' : 'Chat'}
        </Link>
        {configId ? (
          <>
            <ChevronRightIcon className="size-3 shrink-0 text-ink-4" aria-hidden />
            <Link
              to={customAgentConfigHref(configId)}
              className="truncate text-ink-2 hover:underline hover:underline-offset-2"
            >
              {configName ?? 'Config'}
            </Link>
          </>
        ) : null}
        <ChevronRightIcon className="size-3 shrink-0 text-ink-4" aria-hidden />
        <span className="text-ink-4">Run</span>
      </nav>

      <div className="flex items-start gap-4">
        <h1 className="m-0 min-w-0 flex-1 text-22 font-semibold leading-[1.25] tracking-[-0.01em] text-ink">
          {title}
        </h1>
        <div className="flex shrink-0 items-center gap-1.5">
          <StatusPill
            status={tone}
            className={cn(state === 'running' && '[&_[role=status]]:animate-pulse')}
          >
            {label}
          </StatusPill>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-12 text-ink-3">
        {items.map((item, i) => (
          <span key={`${item.text}-${i}`} className="inline-flex items-center gap-3">
            {i > 0 ? <span className="text-ink-4">·</span> : null}
            <span className="tabular-nums" title={item.title}>
              {item.text}
            </span>
          </span>
        ))}

        <span className="ml-auto flex items-center gap-1.5">
          <span
            title={runId}
            className="inline-flex items-center rounded-md border border-line bg-bg-elev pl-2 font-mono text-11 text-ink-3"
          >
            {shortRunId(runId)}
            <CopyButton text={runId} label="Copy run id" className="text-ink-4 hover:text-ink-2" />
          </span>
          {onCancel && (
            <RunCancelButton
              agentLabel="Custom-agent"
              state={state}
              busy={cancelling}
              onCancel={onCancel}
            />
          )}
        </span>
      </div>

      {findingVerdict ? (
        <FindingStatusStrip
          status={completionStatus}
          reason={completionReason}
          completedAtLabel={
            completedAt ? formatRelativeTime(completedAt, { now, fallback: '' }) : undefined
          }
        />
      ) : null}
    </header>
  )
}
